'use client';

import { useRef, useState } from 'react';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import DOMPurify from 'dompurify';

import VideoPlayer from './VideoPlayer';
import YouTubeEmbed from './YoutubeEmbed';
export const MediaPlayer = ({
  type,
  src,
  title = '',
  description,
  width = '100%',
  height = 'auto',
  autoplay = false,
  controls = true,
  className = '',
  hasOwnBold,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(!!src);
  const imgRef = useRef(null);

  const getYouTubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setIsLoading(false);
    setImageError(false);
  };

  const handleImageError = (e) => {
    console.error('Error cargando imagen:', src);
    setImageError(true);
    setIsLoading(false);
    const target = e.target;
    // Usar una imagen placeholder más confiable
    target.src = `data:image/svg+xml;base64,${btoa(`
      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#f0f0f0"/>
        <text x="50%" y="50%" font-family="Arial" font-size="16" fill="#666" text-anchor="middle" dy="0.3em">
          Error al cargar imagen
        </text>
      </svg>
    `)}`;
  };

  const renderContent = () => {
    switch (type) {
      case 'youtube':
        return <YouTubeEmbed src={src} title={title} />;

      case 'video':
        return (
          <Box
            sx={{
              width: '100%',
              minHeight: { xs: 200, sm: 300 },
              backgroundColor: '#000',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <VideoPlayer
              src={src}
              width="100%"
              height="100%"
              controls={controls}
              autoplay={autoplay}
              poster="/placeholder.svg?height=450&width=800&text=Video+Poster"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
              onError={(e) => {
                console.error('Error cargando video:', src);
              }}
            />
          </Box>
        );

      case 'image':
        return (
          <Box
            position="relative"
            bgcolor="#f5f5f5"
            borderRadius={2}
            overflow="hidden"
            sx={{
              width: '100%',
              minHeight: { xs: 200, sm: height || 300 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Mostrar loading solo si está cargando Y no hay error */}
            {isLoading && !imageError && (
              <CircularProgress
                size={40}
                sx={{
                  position: 'absolute',
                  zIndex: 1,
                }}
              />
            )}

            {/* Mostrar error si hay error */}
            {imageError && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ position: 'absolute', zIndex: 1 }}
              >
                Error al cargar imagen
              </Typography>
            )}

            <img
              ref={imgRef}
              src={src}
              alt={title || 'Imagen'}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                // Siempre visible, no ocultar mientras carga
                opacity: imageLoaded ? 1 : 0, // Usar opacity en lugar de display
                transition: 'opacity 0.3s ease', // Suave transición
                minHeight: 'inherit',
              }}
            />
          </Box>
        );
      default:
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'grey.100',
              borderColor: 'grey.300',
              minHeight: { xs: 200, sm: 250 },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'grey.300',
                borderRadius: '50%',
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                ?
              </Typography>
            </Box>
            <Typography fontWeight="medium" gutterBottom>
              Tipo de contenido no reconocido
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tipos soportados: youtube, video, image
            </Typography>
          </Paper>
        );
    }
  };

  return (
    <Box
      width="100%"
      className={className}
      sx={{
        // Evitar desbordamiento en móviles
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      {(title || description) && (
        <Box mb={2}>
          {title && (
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: hasOwnBold(title) ? 'normal' : 'bold',
                mb: description ? 1 : 0,
                wordBreak: 'break-word',
              }}
            >
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }} />
            </Typography>
          )}
          {description && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontStyle: 'italic',
                color: 'text.secondary',
                wordBreak: 'break-word',
              }}
            >
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ width: '100%' }}>{renderContent()}</Box>
    </Box>
  );
};

export default MediaPlayer;
